import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Paper,
  Skeleton
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { EyeIcon, ShoppingIcon } from "../../icons";
import { CheckIcon } from "lucide-react";
import moment from "moment";
import { fetchPaymentTimeline } from "../../api/paymentAnalytics";
import { isPlayRefusal } from "../../utils/paymentReason";

const ACTION_META = {
  VIEW_PAYMENTWALL: { type: "view", title: "Paywall Viewed" },
  START_PAYMENT: { type: "purchase", title: "Purchase Started" },
  PAYMENT_SUCCESS: { type: "success", title: "Payment Success" },
  PAYMENT_CANCELLED: { type: "cancelled", title: "Payment Cancelled" },
  PAYMENT_PENDING: { type: "pending", title: "Awaiting Approval" },
  PAYMENT_FAILED: { type: "failed", title: "Payment Failed" }
};

const statusStyles = {
  view: {
    color: "#2F6FED",
    bg: "#EEF4FF",
    icon: <EyeIcon fontSize="small" />
  },
  purchase: {
    color: "#F97316",
    bg: "#FFF4E6",
    icon: <ShoppingIcon fontSize="small" />
  },
  cancelled: {
    color: "#6B7280",
    bg: "#F3F4F6",
    icon: <CloseIcon fontSize="small" />
  },
  pending: {
    color: "#B45309",
    bg: "#FFFBEB",
    icon: <ShoppingIcon fontSize="small" />
  },
  failed: {
    color: "#EF4444",
    bg: "#FEECEC",
    icon: <CloseIcon fontSize="small" />
  },
  success: {
    color: "#16A34A",
    bg: "#ECFDF5",
    icon: <CheckIcon fontSize="small" />
  }
};

const formatTs = (ts) => (ts ? moment(ts).format("YYYY-MM-DD hh:mm A") : "");

export default function PaymentTimelineModal({ open, onClose, userId, userEmail }) {
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [headerEmail, setHeaderEmail] = useState("");

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setTimeline([]);
        const data = await fetchPaymentTimeline(userId);
        if (cancelled) return;
        const events = (data?.events || []).map((e) => {
          const refused = e.action === "PAYMENT_FAILED" && isPlayRefusal(e.reason);
          const meta = (refused ? ACTION_META.PAYMENT_CANCELLED : ACTION_META[e.action]) || { type: "view", title: e.action };
          return {
            type: meta.type,
            title: meta.title,
            date: formatTs(e.timestamp),
            reason: refused ? null : e.reason || null
          };
        });
        setTimeline(events);
        setHeaderEmail(data?.user?.email || userEmail || "");
      } catch (err) {
        console.error("Error while loading payment timeline", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, userId, userEmail]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: '500px',
        }
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: '20px', color: '#101828' }}>Payment Timeline</Typography>
          <Typography fontSize={14} color="#4A5565">
            {headerEmail || userEmail || ""}
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          maxHeight: 900,
          overflowY: "auto",
          position: "relative",
          pl: 3
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 9 }}>
          <Box
            sx={{
              position: "absolute",
              left: 17,
              top: 0,
              height: '100%',
              width: "2px",
              bgcolor: "#E5E7EB"
            }}
          />

          {loading ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <Box key={idx} sx={{ display: "flex", gap: 2, mb: 3 }}>
                <Skeleton variant="circular" width={36} height={36} />
                <Skeleton variant="rectangular" height={56} sx={{ flex: 1, borderRadius: 2 }} />
              </Box>
            ))
          ) : timeline.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography fontSize={14} color="#6A7282">No payment activity found for this user.</Typography>
            </Box>
          ) : (
            timeline.map((item, index) => {
              const style = statusStyles[item.type];
              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 3,
                    position: "relative"
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: style.color,
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2,
                      flexShrink: 0
                    }}
                  >
                    {style.icon}
                  </Box>

                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      p: '7px 16px',
                      borderRadius: 2,
                      bgcolor: style.bg,
                      border: `1px solid ${style.color}30`
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: 'center',
                        justifyContent: "space-between",
                        mb: item.reason ? 1 : 0
                      }}
                    >
                      <Typography fontWeight={500} sx={{ color: '#101828', fontSize: '16px' }}>{item.title}</Typography>
                      <Typography fontSize={12} color="#6A7282">
                        {item.date}
                      </Typography>
                    </Box>

                    {item.reason && (
                      <Box sx={{ fontSize: 13, color: "#374151" }}>
                        <Typography sx={{ fontSize: '14px', color: '#364153', fontWeight: '400' }}>
                          <span style={{ fontWeight: '500' }}>Reason:</span> {item.reason}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
